import { Box, TextField, Typography, Chip } from "@mui/material";
import type { KeyboardEvent } from "react";
import type { MaterialSupplierForm } from "@/api/materialSupplierService";

interface BasicInfoFieldsProps {
  formData: MaterialSupplierForm;
  setFormData: (data: MaterialSupplierForm) => void;
  phoneInput: string;
  setPhoneInput: (phone: string) => void;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
  materialSupplierId: number | undefined;
}

const BasicInfoFields = ({
  formData,
  setFormData,
  phoneInput,
  setPhoneInput,
  errors,
  setErrors,
  materialSupplierId,
}: BasicInfoFieldsProps) => {
  const handlePhoneKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && phoneInput.trim()) {
      event.preventDefault();
      const newPhone = phoneInput.trim();
      if (!formData.phones.includes(newPhone)) {
        setFormData({
          ...formData,
          phones: [...formData.phones, newPhone],
        });
      }
      setPhoneInput("");
    }
  };

  const removePhone = (phoneToRemove: string) => {
    setFormData({
      ...formData,
      phones: formData.phones.filter((phone) => phone !== phoneToRemove),
    });
  };

  return (
    <>
      <Box display={"flex"} gap={1} mt={2}>
        <TextField
          label={
            <>
              Supplier Code{" "}
              <Typography component="span" color="error.main">
                *
              </Typography>
            </>
          }
          variant="outlined"
          sx={{ width: "25%" }}
          value={formData.name_abbr}
          onChange={(e) => {
            setFormData({
              ...formData,
              name_abbr: e.target.value,
            });
            if (errors.name_abbr) {
              setErrors({ ...errors, name_abbr: "" });
            }
          }}
          error={!!errors.name_abbr}
          helperText={errors.name_abbr}
          disabled={!!materialSupplierId}
        />
        <TextField
          label={
            <>
              Supplier Name{" "}
              <Typography component="span" color="error.main">
                *
              </Typography>
            </>
          }
          variant="outlined"
          sx={{ width: "35%" }}
          value={formData.name}
          onChange={(e) => {
            setFormData({
              ...formData,
              name: e.target.value,
            });
            if (errors.name) {
              setErrors({ ...errors, name: "" });
            }
          }}
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          label={"Email"}
          variant="outlined"
          sx={{ width: "40%" }}
          value={formData.email}
          onChange={(e) => {
            setFormData({
              ...formData,
              email: e.target.value,
            });
          }}
        />
      </Box>
      <Box display={"flex"} gap={1} alignItems="flex-start">
        <TextField
          label={"Address"}
          variant="outlined"
          sx={{ width: "70%" }}
          value={formData.address_text}
          multiline
          minRows={3}
          maxRows={3}
          onChange={(e) => {
            setFormData({
              ...formData,
              address_text: e.target.value,
            });
          }}
        />
        <TextField
          fullWidth
          sx={{ width: "30%" }}
          label={"Tel"}
          variant="outlined"
          value={phoneInput}
          placeholder="กด Enter เพื่อเพิ่มเบอร์โทร"
          onChange={(e) => setPhoneInput(e.target.value)}
          onKeyDown={handlePhoneKeyDown}
          onBlur={() => {
            if (phoneInput.trim()) {
              const newPhone = phoneInput.trim();
              if (!formData.phones.includes(newPhone)) {
                setFormData({
                  ...formData,
                  phones: [...formData.phones, newPhone],
                });
              }
              setPhoneInput("");
            }
          }}
          multiline
          minRows={3}
          maxRows={3}
          slotProps={{
            input: {
              endAdornment: (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mb: 0,
                    width: "80%",
                    maxHeight: "60px",
                    overflow: "auto",
                    padding: "2px",
                  }}
                >
                  {formData.phones.map((phone, index) => (
                    <Chip
                      key={index}
                      label={phone}
                      onDelete={() => removePhone(phone)}
                      sx={{
                        maxWidth: "100%",
                        overflow: "hidden",
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ),
            },
          }}
        />
      </Box>
    </>
  );
};

export default BasicInfoFields;
